// Forked from ../ResultsDropdown/ResultsDropdown.tsx — keep in sync until phase_0 cleanup.
import React from 'react';
import styled from 'styled-components';
import { token } from '@bynder/design-system';
import { HELP_FOOTER_HEIGHT, HelpFooter } from '../Help/HelpFooter';
import { useShowHelpFooter } from '../Help/useShowHelpFooter';
import { CategoryChips } from './CategoryChips';
import { SearchOptionsDropdown } from './SearchOptionsDropdown';
import { ResultsSection } from './Results/ResultsSection';
import { StyledDivider } from './SharedComponents';
import RecentSearches from './RecentSearches';
import { useResultsVisibility } from './useResultsVisibility';
import { ResultsSkeleton } from './Results/ResultsSkeleton';
import { FILTER_ROW_HEIGHT, ResultsCategory, getDynamicDropdownHeight } from './constants';

import {
	useSearchMode,
	useSpotlightDropdownContext,
	useSearch,
	useSearchScope,
} from '../SpotlightState';

// The part of the card below the search bar: filter-row slot, results, help footer.
// Height adapts to which rows are present.
export function SearchDropdown() {
	const { selectedResultsCategory, selectedSearchOptions } = useSearchScope();
	const { searchTerm, isLoadingResults, searchResults } = useSearch();
	const { isDropdownOpen } = useSpotlightDropdownContext();
	const { isDropdownSearchDisabled } = useSearchMode();
	const { showResults, showResultSkeletons, showRecentSearches } = useResultsVisibility({
		searchTerm,
		isLoadingResults,
		isDropdownSearchDisabled,
	});

	const showHelpFooter = useShowHelpFooter();
	const showFilterRow = !isDropdownSearchDisabled; // category chips and Search Options dropdown
	const hasResultsContent = showResultSkeletons || showRecentSearches || showResults;
	const showSearchOptionsDropdown =
		selectedResultsCategory === ResultsCategory.Assets &&
		(selectedSearchOptions.length > 0 || !!searchResults.assets.total);

	const dropdownHeight = getDynamicDropdownHeight({
		hasFilterRow: showFilterRow,
		hasResultsContent,
		hasFooter: showHelpFooter,
	});

	return (
		<StyledOuterDiv id="spotlightContainer" $isOpen={isDropdownOpen} $height={dropdownHeight}>
			{showFilterRow && (
				<StyledSearchFiltersRow>
					<CategoryChips />
					{showSearchOptionsDropdown && <SearchOptionsDropdown />}
				</StyledSearchFiltersRow>
			)}

			{hasResultsContent && <StyledDivider />}

			<StyledResultsWrapper>
				{showResultSkeletons && <ResultsSkeleton />}
				{showRecentSearches && <RecentSearches />}
				{showResults && <ResultsSection />}
			</StyledResultsWrapper>

			{showHelpFooter && (
				<StyledFooterWrapper>
					<HelpFooter showSearchKeyboardShortcut />
				</StyledFooterWrapper>
			)}
		</StyledOuterDiv>
	);
}

const StyledOuterDiv = styled.div<{
	$isOpen: boolean;
	$height: number;
}>`
	display: ${({ $isOpen }) => ($isOpen ? 'flex' : 'none')};
	flex-direction: column;
	height: ${({ $height }) => $height}px;
	transition: height 200ms ease;
	background-color: ${token.white};
	box-sizing: border-box;

	& * {
		box-sizing: border-box;
	}
`;

// Scrolls sideways instead of wrapping, so it keeps the fixed height getDynamicDropdownHeight assumes.
const StyledSearchFiltersRow = styled.div`
	display: flex;
	height: ${FILTER_ROW_HEIGHT}px;
	align-items: center;
	justify-content: space-between;
	gap: ${token.spacing3};
	padding: 0 ${token.spacing5};
	box-sizing: border-box;
	margin-right: calc(-1 * ${token.spacing2});
	overflow-x: auto;
	scrollbar-width: none;

	&::-webkit-scrollbar {
		display: none;
	}

	& > * {
		flex-shrink: 0;
	}
`;

const StyledResultsWrapper = styled.div`
	display: flex;
	flex-direction: column;
	flex: 1;
	min-height: 0;
	padding-top: ${token.spacing4};
	overflow-y: auto;
	overscroll-behavior: contain;
`;

const StyledFooterWrapper = styled.div`
	position: relative;
	height: ${HELP_FOOTER_HEIGHT}px;
	margin-top: ${token.spacing3};
`;
